import React, { useEffect } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, ShoppingBag, Package as PackageIcon, Tag, Ticket, LogOut, ExternalLink, Gift, Building2, Repeat, Users, Flame, BookOpen } from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/pedidos", label: "Pedidos", icon: ShoppingBag },
  { to: "/admin/productos", label: "Productos", icon: Tag },
  { to: "/admin/paquetes", label: "Paquetes", icon: PackageIcon },
  { to: "/admin/cupones", label: "Cupones", icon: Ticket },
  { to: "/admin/tarjetas", label: "Tarjetas regalo", icon: Gift },
  { to: "/admin/corporativo", label: "Corporativo", icon: Building2 },
  { to: "/admin/parrilladas", label: "Parrilladas", icon: Flame },
  { to: "/admin/blog", label: "Blog", icon: BookOpen },
  { to: "/admin/suscripciones", label: "Suscripciones", icon: Repeat },
  { to: "/admin/referidos", label: "Referidos", icon: Users },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login?redirect=/admin");
      return;
    }
    if (user.role !== "admin") {
      navigate("/cuenta");
    }
  }, [user, loading, navigate]);

  if (loading || !user || user.role !== "admin") {
    return (
      <div className="min-h-screen bg-arre-black flex items-center justify-center">
        <div className="text-white/50 font-display tracking-widest uppercase">Cargando admin...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-arre-black flex" data-testid="admin-layout">
      <aside className="w-64 bg-arre-surface border-r border-arre-border flex flex-col sticky top-0 h-screen">
        <div className="p-5 border-b border-arre-border">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="Arre Sonora" className="h-10 w-10 object-contain" />
            <div className="leading-none">
              <div className="font-display text-xl tracking-wider text-white">¡ARRE!</div>
              <div className="font-display text-xl tracking-wider text-arre-red -mt-1">ADMIN</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 font-display tracking-wider uppercase text-sm transition ${
                  isActive ? "bg-arre-red text-white" : "text-white/60 hover:text-white hover:bg-arre-black"
                }`
              }
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-4 h-4" strokeWidth={1.5} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-arre-border space-y-2">
          <div className="text-xs text-white/40 mb-2">
            <div className="text-[10px] tracking-widest uppercase">Conectado como</div>
            <div className="text-white text-sm">{user.name}</div>
          </div>
          <Link
            to="/"
            className="flex items-center gap-2 text-xs text-white/60 hover:text-white"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Ver tienda
          </Link>
          <button
            onClick={() => { logout(); navigate("/"); }}
            className="flex items-center gap-2 text-xs text-white/60 hover:text-arre-red"
            data-testid="admin-logout"
          >
            <LogOut className="w-3.5 h-3.5" /> Cerrar sesión
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
